import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { BNTLogo } from "./layoutChrome";
import { VISOR_MODULE_DISPLAY_NAME } from "@/modules/workModuleCore";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("El enlace no es válido. Solicita uno nuevo.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== pw2) { setError("Las contraseñas no coinciden"); return; }
    if (pw.length < 8) { setError("La contraseña debe tener al menos 8 caracteres"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Contraseña actualizada");
      setTimeout(() => nav("/login"), 2500);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "No se pudo restablecer la contraseña");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-white">
      {/* Left BNT brand */}
      <div className="hidden lg:flex lg:col-span-3 relative bg-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute -top-16 -right-16 w-96 h-96 bg-[#0F2D5C] opacity-95 rotate-12 rounded-md"></div>
        <div className="absolute top-32 -right-8 w-64 h-44 bg-[#3DA873] opacity-90 -rotate-6 rounded-md"></div>
        <div className="absolute -bottom-20 -left-10 w-72 h-44 bg-[#3DA873]/15 -rotate-6 rounded-md"></div>
        <div className="relative z-10"><BNTLogo size="lg" showDefault /></div>
        <div className="relative z-10 max-w-xl space-y-5">
          <h1 className="text-4xl xl:text-5xl font-black leading-[1.05] text-[#0F2D5C]" style={{fontFamily:"'Chivo'", letterSpacing:"-0.02em"}}>
            CREA TU NUEVA<br/>
            <span className="text-[#3DA873]">CONTRASEÑA</span>
          </h1>
          <p className="text-slate-600 text-base">Tu cuenta es segura. Solo tú puedes restablecer tu acceso desde tu correo.</p>
        </div>
        <div className="relative z-10 text-[11px] text-slate-500">
          <ShieldCheck className="w-3.5 h-3.5 inline mr-1 text-[#3DA873]" /> {VISOR_MODULE_DISPLAY_NAME} · Acceso seguro
        </div>
      </div>

      <div className="lg:col-span-2 bg-[#F4F6F8] flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-5 bg-white rounded-md border border-slate-200 p-7 shadow-sm" data-testid="reset-form">
          <div className="lg:hidden mb-2"><BNTLogo size="md" showDefault /></div>
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-[#0F2D5C]" style={{fontFamily:"'Chivo'"}}>Restablecer contraseña</h2>
            <p className="text-sm text-slate-500 mt-1">Define tu nueva contraseña.</p>
          </div>

          {done ? (
            <div className="flex flex-col items-center text-center gap-2 py-4">
              <CheckCircle2 className="w-10 h-10 text-[#3DA873]"/>
              <p className="text-sm text-slate-700">¡Contraseña actualizada! Te redirigimos al login...</p>
            </div>
          ) : (
            <>
              <div>
                <Label className="text-xs font-medium text-slate-700">Nueva contraseña</Label>
                <Input data-testid="reset-pw1" type="password" value={pw} onChange={(e)=>setPw(e.target.value)} required minLength={8}
                  className="mt-1 rounded-sm font-mono" placeholder="••••••••"/>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Confirmar contraseña</Label>
                <Input data-testid="reset-pw2" type="password" value={pw2} onChange={(e)=>setPw2(e.target.value)} required minLength={8}
                  className="mt-1 rounded-sm font-mono" placeholder="••••••••"/>
              </div>
              {error && <div data-testid="reset-error" className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">{error}</div>}
              <Button data-testid="reset-submit" type="submit" disabled={busy || !token}
                className="w-full bg-[#0F2D5C] hover:bg-[#0a1f42] rounded-sm h-10 border-b-2 border-[#3DA873]">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Actualizar contraseña
              </Button>
              <div className="text-center text-xs text-slate-500">
                <Link to="/login" className="text-[#0F2D5C] font-medium underline">Volver al inicio de sesión</Link>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
